// --------------------------------------------------------------
// Created On: 2024-05-03
// Author: KC Willard
//
// Last Modified: 2025-11-06
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React from "react";
import { COUNTRY_LIST, STATES_LIST } from "../../../constants/miscellaneous";
import Warning from "../../../components/Warning/Warning";
import Error from "../../../components/Error/Error";
import styles from "./CustomerForm.module.scss";

// Form for users to fill in information about the customer they are shipping the product to.
export default function CustomerForm(props: Props): Component {
  // Manage the state of the selected customer in the drop down menu.
  function handleSelectSavedCustomerAddress(savedCustomerAddressName: string) {
    const savedCustomerAddress = props.savedCustomers.find((element) => element.name === savedCustomerAddressName);
    if (savedCustomerAddress !== undefined) {
      props.onChangeSelectedCustomerAddress(savedCustomerAddress);
    }
  }

  return (
    <div className="px-3">
      <h6>
        All fields are required. Edit as necessary. This is the person we will contact if we have questions about your
        order.
      </h6>
      <div className={`${styles.inputRow} row`}>
        <input
          className="form-control mx-auto"
          data-test="username-input"
          type="text"
          disabled={true}
          value={props.name}
        />
      </div>
      <div className={`${styles.inputRow} row`}>
        <input
          className="form-control mx-auto"
          data-test="company-name-input"
          type="text"
          disabled={true}
          value={props.companyName}
        />
      </div>
      <div className={`${styles.inputRow} row`}>
        <input
          className="form-control mx-auto"
          data-test="email-input"
          type="text"
          disabled={true}
          value={props.email}
        />
      </div>
      <div className={`${styles.inputRow} row`}>
        <input
          className="form-control mx-auto"
          data-test="phone-number-input"
          type="text"
          disabled={true}
          value={props.phoneNumber}
        />
      </div>
      <div className={`${styles.inputRow} row`}>
        <input
          className="form-control mx-auto"
          data-test="po-number-input"
          type="text"
          placeholder="PO #"
          value={props.poNumber}
          onChange={(e) => props.onChangePoNumber(e.target.value)}
        />
      </div>
      {props.isQuote && (
        <div className={`${styles.inputRow} row`}>
          <input
            className="form-control mx-auto"
            data-test="quote-name-input"
            type="text"
            placeholder="Quote Name"
            value={props.quoteName}
            onChange={(e) => props.onChangeQuoteName(e.target.value)}
          />
        </div>
      )}
      <div className="d-none d-lg-block">
        <div className={`${styles.storedCustomerRow} row`}>
          <div className="col-2 col-lg-5" style={{ float: "left", paddingLeft: "0" }}>
            <button
              className="btn btn-primary"
              disabled={props.selectedCustomerAddress === null}
              onClick={() => props.getSavedCustomerAddressInformation()}
            >
              Load Customer Information
            </button>
          </div>
          <div className="col-2 col-lg-6">
            <select
              data-test="saved-customer-selection"
              className="form-select"
              value={props.selectedCustomerAddress !== null ? props.selectedCustomerAddress.name : ""}
              onChange={(e) => handleSelectSavedCustomerAddress(e.target.value)}
            >
              <option value="" disabled>
                Select a saved customer to load shipping information
              </option>
              {props.savedCustomers.map((savedCustomer) => (
                <option value={savedCustomer.name} key={savedCustomer.savedCustomerAddressId}>
                  {savedCustomer.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-1" style={{ float: "right", paddingRight: "0" }}>
            <button
              className="btn btn-danger"
              style={{ float: "right" }}
              disabled={props.selectedCustomerAddress === null}
              onClick={() => props.onChangeShowDeleteSavedCustomerModal(true)}
              data-test="item-delete-button"
            >
              <i className="fa fa-trash" />
            </button>
          </div>
        </div>
      </div>
      {/* Mobile display of saved customers Get/Delete */}
      <div className="d-block d-lg-none">
        <div className={`${styles.mobileStoredCustomerRow} row`}>
          <div className="col">
            <select
              data-test="saved-customer-selection"
              className="form-select"
              value={props.selectedCustomerAddress !== null ? props.selectedCustomerAddress.name : ""}
              onChange={(e) => handleSelectSavedCustomerAddress(e.target.value)}
            >
              <option value="" disabled>
                Select a saved customer to load shipping information
              </option>
              {props.savedCustomers.map((savedCustomer) => (
                <option value={savedCustomer.name} key={savedCustomer.savedCustomerAddressId}>
                  {savedCustomer.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className={`${styles.mobileStoredCustomerRow} row`}>
          <div className="col">
            <button
              className="btn btn-primary"
              disabled={props.selectedCustomerAddress === null}
              onClick={() => props.getSavedCustomerAddressInformation()}
            >
              Load Customer
            </button>
          </div>
          <div className="col" style={{ float: "right", paddingRight: "0" }}>
            <button
              className="btn btn-danger"
              disabled={props.selectedCustomerAddress === null}
              onClick={() => props.onChangeShowDeleteSavedCustomerModal(true)}
              data-test="item-delete-button"
            >
              Delete Customer
            </button>
          </div>
        </div>
      </div>

      <div className={`${styles.inputRow} row`}>
        <select
          data-test="shipping-country-selection"
          className="form-select"
          value={props.shippingCountry}
          onChange={(e) => props.onChangeShippingCountry(e.target.value)}
        >
          {COUNTRY_LIST.map((country) => (
            <option value={country.code} key={country.code}>
              {country.name}
            </option>
          ))}
        </select>
      </div>
      <div className={`${styles.inputRow} row`}>
        <input
          className={`form-control mx-auto ${props.shippingCompanyName.length > 50 ? styles.inputError : ""}`}
          data-test="shipping-company-name-input"
          type="text"
          placeholder="Company Name"
          value={props.shippingCompanyName}
          onChange={(e) => props.onChangeShippingCompanyName(e.target.value)}
        />
        <div className="text-danger small mt-1">
          {props.shippingCompanyName.length > 50 && (
            <span>
              Company Name must be 50 characters or less. Currently {props.shippingCompanyName.length} characters.
            </span>
          )}
        </div>
      </div>
      <div className={`${styles.inputRow} row`}>
        <input
          className={`form-control mx-auto ${props.shippingAddressLine1.length > 50 ? styles.inputError : ""}`}
          data-test="shipping-address-line-1-input"
          type="text"
          placeholder="Address Line 1"
          value={props.shippingAddressLine1}
          onChange={(e) => props.onChangeShippingAddressLine1(e.target.value)}
        />
        <div className="text-danger small mt-1">
          {props.shippingAddressLine1.length > 50 && (
            <span>
              Shipping Address Line 1 must be 50 characters or less. Currently {props.shippingAddressLine1.length}{" "}
              characters.
            </span>
          )}
        </div>
      </div>
      <div className={`${styles.inputRow} row`}>
        <input
          className={`form-control mx-auto ${props.shippingAddressLine2.length > 50 ? styles.inputError : ""}`}
          data-test="shipping-address-line-1-input"
          type="text"
          placeholder="Address Line 2 (optional)"
          value={props.shippingAddressLine2}
          onChange={(e) => props.onChangeShippingAddressLine2(e.target.value)}
        />
        <div className="text-danger small mt-1">
          {props.shippingAddressLine2.length > 50 && (
            <span>
              Shipping Address Line 2 must be 50 characters or less. Currently {props.shippingAddressLine2.length}{" "}
              characters.
            </span>
          )}
        </div>
      </div>
      <div className={`${styles.inputRow} row `}>
        <div className="col" style={{ float: "left", paddingLeft: "0" }}>
          <input
            className={`form-control mx-auto ${props.shippingContactName.length > 50 ? styles.inputError : ""}`}
            data-test="shipping-contact-name-input"
            type="text"
            placeholder="Contact Name (optional)"
            value={props.shippingContactName}
            onChange={(e) => props.onChangeShippingContactName(e.target.value)}
          />
          <div className="text-danger small mt-1">
            {props.shippingContactName.length > 50 && (
              <span>
                Shipping Contact Name must be 50 characters or less. Currently {props.shippingContactName.length}{" "}
                characters.
              </span>
            )}
          </div>
        </div>
        <div className="col" style={{ float: "right", paddingRight: "0" }}>
          <input
            className={`form-control mx-auto ${props.shippingPhoneNumber.length > 20 ? styles.inputError : ""}`}
            data-test="shipping-phone-number-input"
            type="text"
            placeholder="Phone Number (optional)"
            value={props.shippingPhoneNumber}
            onChange={(e) => props.onChangeShippingPhoneNumber(e.target.value)}
          />
          <div className="text-danger small mt-1">
            {props.shippingPhoneNumber.length > 20 && (
              <span>
                Shipping Phone Number must be 20 characters or less. Currently {props.shippingPhoneNumber.length}{" "}
                characters.
              </span>
            )}
          </div>
        </div>
      </div>
      <div className={`${styles.inputRow} row`}>
        <div className="col" style={{ float: "left", paddingLeft: "0" }}>
          <input
            className={`form-control mx-auto ${props.shippingCity.length > 50 ? styles.inputError : ""}`}
            data-test="shipping-city-input"
            type="text"
            placeholder="City"
            value={props.shippingCity}
            onChange={(e) => props.onChangeShippingCity(e.target.value)}
          />
          <div className="text-danger small mt-1">
            {props.shippingCity.length > 50 && (
              <span>
                Shipping City must be 50 characters or less. Currently {props.shippingCity.length} characters.
              </span>
            )}
          </div>
        </div>
        <div className="col">
          {props.shippingCountry === "US" ? (
            <select
              data-test="shipping-state-selection"
              className="form-select"
              value={props.shippingState}
              onChange={(e) => props.onChangeShippingState(e.target.value)}
            >
              <option value="">Please select a state...</option>
              {STATES_LIST.map((state) => (
                <option value={state.code} key={state.code}>
                  {state.name}
                </option>
              ))}
            </select>
          ) : (
            <input
              className="form-control mx-auto"
              data-test="shipping-state-input"
              type="text"
              placeholder="State/Province"
              value={props.shippingState}
              onChange={(e) => props.onChangeShippingState(e.target.value)}
            />
          )}
        </div>
        <div className="col" style={{ float: "right", paddingRight: "0" }}>
          <input
            className={`form-control mx-auto ${props.errorMessage.length > 0 ? styles.inputError : ""}`}
            data-test="shipping-zip-code-input"
            type="text"
            placeholder="Zip"
            value={props.shippingZipCode}
            onChange={(e) => props.onChangeShippingZipCode(e.target.value)}
            onBlur={() => props.onZipCodeInputBlur()}
            required
          />
        </div>
      </div>

      <div className={`${styles.commentRow} row`}>
        <label style={{ fontSize: "large", fontWeight: "bold" }}>Comments</label>
        <textarea
          className="form-control mx-auto"
          data-test="customer-comments-input"
          value={props.customerComment}
          onChange={(e) => props.onChangeCustomerComment(e.target.value)}
        />
      </div>

      <div className="my-2">
        <Error message={props.errorMessage} />
        <Warning message={props.warningMessage} />
      </div>

      <div className={`${styles.buttonRow} row`}>
        <div className="col-auto col-lg-3">
          <button
            className="btn btn-primary"
            onClick={() => props.onChangeSetShowSavedCustomerModal(true)}
            style={{ display: "block", width: "100%" }}
            disabled={!props.validateShippingInformation}
          >
            Save Customer Information
          </button>
        </div>
      </div>
    </div>
  );
}

interface Props {
  name: string;
  companyName: string;
  email: string;
  phoneNumber: string;
  savedCustomers: SavedCustomer[];
  poNumber: string;
  quoteName: string;
  isQuote: boolean;
  shippingCountry: string;
  shippingCompanyName: string;
  shippingAddressLine1: string;
  shippingAddressLine2: string;
  shippingContactName: string;
  shippingPhoneNumber: string;
  shippingCity: string;
  shippingState: string;
  shippingZipCode: string;
  validateShippingInformation: boolean;
  selectedCustomerAddress: SavedCustomer | null;
  customerComment: string;
  errorMessage: string;
  warningMessage: string;
  onChangePoNumber: (poNumber: string) => void;
  onChangeQuoteName: (quoteName: string) => void;
  onChangeShippingCountry: (shippingCountry: string) => void;
  onChangeShippingCompanyName: (shippingCompanyName: string) => void;
  onChangeShippingAddressLine1: (shippingAddressLine1: string) => void;
  onChangeShippingAddressLine2: (shippingAddressLine2: string) => void;
  onChangeShippingContactName: (shippingContactName: string) => void;
  onChangeShippingPhoneNumber: (shippingPhoneNumber: string) => void;
  onChangeShippingCity: (shippingCity: string) => void;
  onChangeShippingState: (shippingState: string) => void;
  onChangeShippingZipCode: (shippingZipCode: string) => void;
  onChangeCustomerComment: (customerComment: string) => void;
  getSavedCustomerAddressInformation: () => void;
  onChangeShowDeleteSavedCustomerModal: (showDeleteCustomerModal: boolean) => void;
  onChangeSelectedCustomerAddress: (selectedCustomerAddress: SavedCustomer) => void;
  onChangeSetShowSavedCustomerModal: (showSavedCustomerModal: boolean) => void;
  onZipCodeInputBlur: () => void;
}

interface SavedCustomer {
  savedCustomerAddressId: number;
  name: string;
}
